import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import FlagIcon from '../FlagIcon';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback languages if API fails
const FALLBACK_LANGUAGES = [
  { code: 'pl', nativeName: 'Polski', flag: '🇵🇱', isDefault: true },
  { code: 'en', nativeName: 'English', flag: '🇬🇧', isDefault: false },
];

/**
 * LanguageTabs - Reusable component for multi-language content editing
 * 
 * Usage:
 * <LanguageTabs
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   showIndicator={true}
 *   hasContent={{ pl: true, en: false }}
 * />
 */
const LanguageTabs = ({ 
  activeTab, 
  onTabChange, 
  className = '',
  showIndicator = false,
  hasContent = {},
  size = 'default' // 'default' | 'small'
}) => {
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        // Get admin token - stored as 'admin_token' in localStorage
        const token = localStorage.getItem('admin_token');
        let response;
        
        if (token) {
          // Use admin endpoint which returns ALL languages including inactive
          try {
            response = await axios.get(`${API}/api/admin/languages`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (e) {
            console.warn('Admin languages endpoint failed, falling back to public');
            response = await axios.get(`${API}/api/languages`);
          }
        } else {
          response = await axios.get(`${API}/api/languages`);
        }
        
        if (response.data && response.data.length > 0) {
          // Map and sort - default first, then by name
          // Include ALL languages (active and inactive) for admin editing
          const langs = response.data
            .map(l => ({
              code: l.code,
              nativeName: l.nativeName,
              flag: l.flag || '🏳️',
              isDefault: l.isDefault || false,
              isActive: l.isActive !== false
            }))
            .sort((a, b) => {
              if (a.isDefault) return -1;
              if (b.isDefault) return 1;
              return a.nativeName.localeCompare(b.nativeName);
            });
          
          setLanguages(langs);
          
          // If current activeTab is not in available languages, switch to default
          if (activeTab && !langs.find(l => l.code === activeTab)) {
            const defaultLang = langs.find(l => l.isDefault) || langs[0];
            if (defaultLang && onTabChange) {
              onTabChange(defaultLang.code);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch languages for tabs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLanguages();
  }, []);

  if (loading) {
    return (
      <div className={cn("flex items-center gap-2 text-gray-400", className)}>
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Ładowanie języków...</span>
      </div>
    );
  }

  const sizeClasses = size === 'small' 
    ? 'px-2 py-1 text-xs gap-1' 
    : 'px-3 py-2 text-sm gap-2';

  return (
    <div className={cn("flex items-center gap-1 flex-wrap", className)}>
      {languages.map((lang) => {
        const isActive = activeTab === lang.code;
        const contentExists = hasContent[lang.code];
        
        return (
          <button
            key={lang.code}
            type="button"
            onClick={() => onTabChange(lang.code)}
            className={cn(
              "flex items-center rounded-lg transition-all font-medium",
              sizeClasses,
              isActive 
                ? "bg-primary text-white shadow-md" 
                : "bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white",
              showIndicator && contentExists === false && "ring-1 ring-yellow-500/50"
            )}
            title={showIndicator && contentExists === false ? `Brak treści w ${lang.nativeName}` : lang.nativeName}
          >
            <FlagIcon code={lang.code} size={16} />
            <span>{lang.nativeName}</span>
            {!lang.isActive && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-gray-500/30 text-gray-400 ml-1" title="Język nieaktywny">
                OFF
              </span>
            )}
            {lang.isDefault && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-white/20 ml-1">
                DOM
              </span>
            )}
            {showIndicator && contentExists === false && (
              <span className="w-2 h-2 rounded-full bg-yellow-500 ml-1" title="Brak tłumaczenia" />
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Hook to get available languages for forms
 */
export const useAvailableLanguages = () => {
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [loading, setLoading] = useState(true);
  const [defaultLanguage, setDefaultLanguage] = useState('pl');

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        // Get admin token - stored as 'admin_token' in localStorage
        const token = localStorage.getItem('admin_token');
        let response;
        
        if (token) {
          // Use admin endpoint which returns ALL languages including inactive
          try {
            response = await axios.get(`${API}/api/admin/languages`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (e) {
            console.warn('Admin languages endpoint failed, falling back to public');
            response = await axios.get(`${API}/api/languages`);
          }
        } else {
          response = await axios.get(`${API}/api/languages`);
        }
        
        if (response.data && response.data.length > 0) {
          // Include ALL languages (active and inactive) for admin editing
          const langs = response.data
            .map(l => ({
              code: l.code,
              nativeName: l.nativeName,
              flag: l.flag || '🏳️',
              isDefault: l.isDefault || false,
              isActive: l.isActive !== false // true if undefined or true
            }));
          
          setLanguages(langs);
          
          const defaultLang = langs.find(l => l.isDefault);
          if (defaultLang) {
            setDefaultLanguage(defaultLang.code);
          }
        }
      } catch (error) {
        console.error('Failed to fetch languages:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLanguages();
  }, []);

  return { languages, loading, defaultLanguage };
};

/**
 * Helper to initialize multi-language field with empty values for all languages
 */
export const initMultiLangField = (languages, defaultValue = '') => {
  const field = {};
  languages.forEach(lang => {
    field[lang.code] = defaultValue;
  });
  return field;
};

/**
 * Get content with fallback to default language
 */
export const getContentWithFallback = (content, lang, defaultLang = 'pl') => {
  if (!content) return '';
  if (content[lang]) return content[lang];
  if (content[defaultLang]) return content[defaultLang];
  // Return first available content
  const firstAvailable = Object.values(content).find(v => v);
  return firstAvailable || '';
};

export default LanguageTabs;
